import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PrintIcon from '@mui/icons-material/Print';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import { getAuthHeaders, getImageDisplayUrl } from '../../utils/api';

// Map routes to page titles
const getPageTitle = (pathname) => {
  const routeMap = {
    '/home': 'Tripploggar',
    '/createlog': 'Skapa logg',
    '/updatelog': 'Uppdatera logg',
    '/showlog': 'Visa logg',
    '/createservicelog': 'Skapa servicelogg',
    '/updateservicelog': 'Uppdatera servicelogg',
    '/showservicelog': 'Visa servicelogg',
    '/servicelogs': 'Serviceloggar',
    '/showprofile': 'Profil',
    '/changeprofile': 'Ändra profil',
    '/signup': 'Registrera',
    '/logs': 'Loggar',
    '/changeuser': 'Ändra konto',
    '/admin': 'Adminpanel',
    '/invitations': 'Inbjudningar',
    '/subscription': 'Prenumerationsplaner',
    '/about': 'Om oss',
    '/terms': 'Användarvillkor',
    '/how-it-works': 'Så här fungerar det',
    '/pricing': 'Priser',
    '/privacy': 'Integritetspolicy',
  };

  // Check for exact matches first
  if (routeMap[pathname]) {
    return routeMap[pathname];
  }

  // Check for routes with parameters
  for (const [route, title] of Object.entries(routeMap)) {
    if (pathname.startsWith(route)) {
      return title;
    }
  }

  return 'LOGGBOKEN';
};

// Check if current route is a start page
const isStartPage = (pathname) => {
  return pathname === '/' || pathname === '/info';
};

// Navigation links for logged-out users
const publicNavLinks = [
  { label: 'Priser', path: '/pricing' },
  { label: 'Så här fungerar det', path: '/how-it-works' },
  { label: 'Om oss', path: '/about' },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, isLoggedIn, logout } = useAuth();
  const { logbooks, currentLogbookId, setCurrentLogbookId } = useLogbook();
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);
  const [profilePicture, setProfilePicture] = React.useState(null);
  const [userInitials, setUserInitials] = React.useState('U');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [hasPremium, setHasPremium] = React.useState(false);
  const profileMenuOpen = Boolean(profileAnchorEl);

  // Hämta prenumerationsstatus för att visa Inbjudningar endast för Premium
  React.useEffect(() => {
    if (!isLoggedIn) {
      setHasPremium(false);
      return;
    }
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/status`, { headers: getAuthHeaders() })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => setHasPremium(!!(data && data.hasActiveOrPendingPremium)))
      .catch(() => setHasPremium(false));
  }, [isLoggedIn]);

  // Hämta profilbild när användaren är inloggad eller när location ändras (för att uppdatera efter profiländringar)
  React.useEffect(() => {
    if (isLoggedIn && currentUser) {
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/showprofile/${currentUser}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch profile');
          }
          return response.json();
        })
        .then(data => {
          // Hantera profilePictures - kan vara array, null eller undefined
          let profilePicturesArray = data.profilePictures || [];
          if (typeof profilePicturesArray === 'string') {
            try {
              profilePicturesArray = JSON.parse(profilePicturesArray);
            } catch (e) {
              profilePicturesArray = [];
            }
          }
          if (!Array.isArray(profilePicturesArray)) {
            profilePicturesArray = [];
          }
          
          // Sätt profilbilden (första bilden i arrayen om den finns)
          if (profilePicturesArray.length > 0) {
            setProfilePicture(profilePicturesArray[0]);
          } else {
            setProfilePicture(null);
          }
          
          // Sätt initialer för Avatar
          const firstName = data.firstName?.[0] || '';
          const lastName = data.lastName?.[0] || '';
          const username = data.username?.[0] || 'U';
          setUserInitials((firstName + lastName || username).toUpperCase());
        })
        .catch(error => {
          console.error('Error fetching profile:', error);
          setProfilePicture(null);
        });
    } else {
      setProfilePicture(null);
    }
  }, [isLoggedIn, currentUser, location.pathname]);

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePrint = () => {
    window.print();
  };

  const isStart = isStartPage(location.pathname);
  const pageTitle = getPageTitle(location.pathname);
  const isHomePage = location.pathname === '/home';

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: 'rgba(21, 101, 192, 0.5)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Left side - Logo or Back button */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', minWidth: { xs: 'auto', md: '200px' } }}>
              {!isStart && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="back"
                  component={Link}
                  to="/info"
                  sx={{ mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              {isStart && (
                <Link to="/info" style={{ color: 'inherit', textDecoration: 'none' }}>
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{ fontWeight: 600 }}
                  >
                    SEGLINGSSAJTEN
                  </Typography>
                </Link>
              )}
            </Box>

            {/* Center - Navigation links for logged-out users on start pages (desktop only) */}
            {isStart && !isLoggedIn && (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                  gap: 3,
                  flex: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {publicNavLinks.map((link) => (
                  <Button
                    key={link.path}
                    component={Link}
                    to={link.path}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Center - Page title (only on non-start pages, but not for /home and /servicelogs) */}
            {!isStart && location.pathname !== '/home' && location.pathname !== '/servicelogs' && (
              <Typography
                variant="h6"
                component="div"
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                {pageTitle}
              </Typography>
            )}

            {/* Spacer for /home and /servicelogs pages */}
            {!isStart && (location.pathname === '/home' || location.pathname === '/servicelogs') && (
              <Box sx={{ flex: 1 }} />
            )}

            {/* Spacer for start pages when logged in (no center nav) */}
            {isStart && isLoggedIn && <Box sx={{ flex: 1 }} />}

            {/* Right side - Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 'auto', md: '200px' }, justifyContent: 'flex-end' }}>
              {isLoggedIn && logbooks.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 140, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' }, '& .MuiInputLabel-root, & .MuiSelect-select': { color: 'white' }, '& .MuiSvgIcon-root': { color: 'white' } }}>
                  <InputLabel id="logbook-select-label">Loggbok</InputLabel>
                  <Select
                    labelId="logbook-select-label"
                    value={currentLogbookId || ''}
                    label="Loggbok"
                    onChange={(e) => setCurrentLogbookId(e.target.value)}
                    sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } }}
                  >
                    {logbooks.map((lb) => (
                      <MenuItem key={lb._id} value={lb._id}>
                        {lb.name || (lb.isOwner ? 'Min loggbok' : 'Loggbok')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!isStart && (
                <>
                  {/* Print button - only show on pages where it makes sense */}
                  {(location.pathname.includes('/show') || location.pathname.includes('/servicelogs') || isHomePage) && (
                    <IconButton
                      color="inherit"
                      aria-label="print"
                      onClick={handlePrint}
                      sx={{ mr: 1 }}
                    >
                      <PrintIcon />
                    </IconButton>
                  )}

                  {/* Profile menu for logged in users */}
                  {isLoggedIn && (
                    <>
                      <IconButton
                        color="inherit"
                        aria-label="profile"
                        onClick={handleProfileMenuOpen}
                        aria-controls={profileMenuOpen ? 'profile-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={profileMenuOpen ? 'true' : undefined}
                        sx={{ p: 0.5 }}
                      >
                        {profilePicture ? (
                          <Avatar
                            src={getImageDisplayUrl(profilePicture)}
                            alt="Profilbild"
                            sx={{ width: 32, height: 32 }}
                          />
                        ) : (
                          <AccountCircleIcon sx={{ fontSize: 32, color: 'white' }} />
                        )}
                      </IconButton>
                      <Menu
                        id="profile-menu"
                        anchorEl={profileAnchorEl}
                        open={profileMenuOpen}
                        onClose={handleProfileMenuClose}
                        MenuListProps={{
                          'aria-labelledby': 'profile-button',
                        }}
                      >
                        {currentUser && (
                          <MenuItem
                            component={Link}
                            to={`/showprofile/${currentUser}`}
                            onClick={handleProfileMenuClose}
                          >
                            Visa profil
                          </MenuItem>
                        )}
                        {currentUser && (
                          <MenuItem
                            component={Link}
                            to={`/changeprofile/${currentUser}`}
                            onClick={handleProfileMenuClose}
                          >
                            Ändra profil
                          </MenuItem>
                        )}
                        <MenuItem
                          component={Link}
                          to="/subscription"
                          onClick={handleProfileMenuClose}
                        >
                          Prenumerationsplaner
                        </MenuItem>
                        {(userRole === 'admin' || userRole === 'superadmin') && (
                          <MenuItem
                            component={Link}
                            to="/admin"
                            onClick={handleProfileMenuClose}
                          >
                            Kontrollpanel
                          </MenuItem>
                        )}
                        {(hasPremium || userRole === 'superadmin') && (
                          <MenuItem
                            component={Link}
                            to="/invitations"
                            onClick={handleProfileMenuClose}
                          >
                            Inbjudningar
                          </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout}>
                          Logga ut
                        </MenuItem>
                      </Menu>
                    </>
                  )}

                  {/* Login button if not logged in */}
                  {!isLoggedIn && (
                    <Button
                      color="inherit"
                      component={Link}
                      to="/"
                      sx={{ textTransform: 'none' }}
                    >
                      Logga in
                    </Button>
                  )}
                </>
              )}

              {/* For start pages - Profile menu for logged in users */}
              {isStart && isLoggedIn && (
                <>
                  <IconButton
                    color="inherit"
                    aria-label="profile"
                    onClick={handleProfileMenuOpen}
                    aria-controls={profileMenuOpen ? 'profile-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={profileMenuOpen ? 'true' : undefined}
                    sx={{ p: 0.5 }}
                  >
                    {profilePicture ? (
                        <Avatar
                        src={getImageDisplayUrl(profilePicture)}
                        alt="Profilbild"
                        sx={{ width: 32, height: 32 }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ fontSize: 32, color: 'white' }} />
                    )}
                  </IconButton>
                  <Menu
                    id="profile-menu"
                    anchorEl={profileAnchorEl}
                    open={profileMenuOpen}
                    onClose={handleProfileMenuClose}
                    MenuListProps={{
                      'aria-labelledby': 'profile-button',
                    }}
                  >
                    {currentUser && (
                      <MenuItem
                        component={Link}
                        to={`/showprofile/${currentUser}`}
                        onClick={handleProfileMenuClose}
                      >
                        Visa profil
                      </MenuItem>
                    )}
                    {currentUser && (
                      <MenuItem
                        component={Link}
                        to={`/changeprofile/${currentUser}`}
                        onClick={handleProfileMenuClose}
                      >
                        Ändra profil
                      </MenuItem>
                    )}
                    <MenuItem
                      component={Link}
                      to="/subscription"
                      onClick={handleProfileMenuClose}
                    >
                      Prenumerationsplaner
                    </MenuItem>
                    {(userRole === 'admin' || userRole === 'superadmin') && (
                      <MenuItem
                        component={Link}
                        to="/admin"
                        onClick={handleProfileMenuClose}
                      >
                        Kontrollpanel
                      </MenuItem>
                    )}
                    {(hasPremium || userRole === 'superadmin') && (
                      <MenuItem
                        component={Link}
                        to="/invitations"
                        onClick={handleProfileMenuClose}
                      >
                        Inbjudningar
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      Logga ut
                    </MenuItem>
                  </Menu>
                </>
              )}

              {/* For start pages - Hamburger menu for mobile (logged out); no Login button - dialog on page */}
              {isStart && !isLoggedIn && (
                <IconButton
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMobileMenuToggle}
                  sx={{ display: { xs: 'flex', md: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      {/* Mobile navigation drawer for logged-out users */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#fff',
          },
        }}
      >
        <Box sx={{ pt: 2 }}>
          <List>
            {publicNavLinks.map((link) => (
              <ListItem key={link.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={link.path}
                  onClick={handleMobileMenuClose}
                >
                  <ListItemText 
                    primary={link.label} 
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        color: '#1565C0',
                        fontWeight: 500,
                      } 
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
